import { useMutation } from '@tanstack/react-query';
import { ShoppingBasket, BookOpen, Network, FileSpreadsheet } from 'lucide-react';
import { ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { ActionDto, HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { KnowledgeBaseActionDto } from '@/Domain/api/Action/dto/KnowledgeBaseAction.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { ToolIcon } from '@/Domain/components/icons/Tool.icon';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useActionLimit } from '@/Domain/hooks/Action/useActionLimit.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { isQuickchatEmail } from '@/Domain/utils/isQuickchatEmail';

type Props = {
  actions: ActionDto[];
};

type CreateActionMenuItemProps = {
  children: ReactNode;
  isPending?: boolean;
  isUnavailable: boolean;
  label: string;
  onClick: () => void | Promise<void>;
  testId: string;
};

const CreateActionMenuItem = ({
  children,
  isPending = false,
  isUnavailable,
  label,
  onClick,
  testId,
}: CreateActionMenuItemProps) => {
  const descriptionId = useId();

  return (
    <Tooltip open={isUnavailable ? undefined : false}>
      <TooltipTrigger asChild>
        <span className="block">
          <DropdownMenuItem
            data-testid={testId}
            aria-disabled={isUnavailable || isPending || undefined}
            aria-describedby={isUnavailable ? descriptionId : undefined}
            data-disabled={isUnavailable ? '' : undefined}
            disabled={isPending}
            onSelect={(event) => {
              if (isUnavailable) event.preventDefault();
            }}
            onClick={() => {
              if (isUnavailable) return;
              void onClick();
            }}
          >
            {children}
          </DropdownMenuItem>
          {isUnavailable && (
            <span id={descriptionId} className="sr-only">
              {label}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent data-testid={`${testId}-tooltip`} align="end" className="z-[110]">
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export const ActionsCreateButton = ({ actions }: Props) => {
  const { createLimit: createdActionLimit } = useActionLimit();
  const { t } = useTranslation('actions_page');
  const { toggleModal } = useModalStore();
  const { user } = useUser();
  const { capture } = usePostHogAnalytics();

  const createKnowledgeBaseActionMutation = useMutation<KnowledgeBaseActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.knowledgeBase.create],
  });
  const handleCreateKnowledgeBaseAction = async () => {
    const knowledgeBaseAction = await createKnowledgeBaseActionMutation.mutateAsync();
    capture(PostHogEventEnum.ActionCreated, { action_type: 'knowledge_base' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'knowledge_base' });
    toggleModal(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET, { knowledgeBaseAction });
  };

  const createShopifyMCPActionMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });
  const handleCreateShopifyMCPAction = async () => {
    const shopifyMcpAction = await createShopifyMCPActionMutation.mutateAsync();
    capture(PostHogEventEnum.ActionCreated, { action_type: 'shopify_mcp' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'shopify_mcp' });
    toggleModal(MODAL_NAMES.SHOPIFY_MCP_DIALOG, { shopifyMcpAction });
  };

  const createHttpRequestActionMutation = useMutation<HttpActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.httpRequest.create],
  });

  const hasReachedActionLimit = (actions || []).length >= createdActionLimit;
  const hasShopifyAction = (actions || []).some(
    (action) => action.type === ActionTypeEnum.ShopifyRemoteMcp,
  );

  const handleCreateRemoteMCPAction = () => {
    capture(PostHogEventEnum.ActionCreated, { action_type: 'remote_mcp' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'remote_mcp' });
    toggleModal(MODAL_NAMES.REMOTE_MCP_DIALOG);
  };

  return (
    <TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="add-action-button">
            <AddIcon />
            {t('button.add_action')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="flex flex-col space-y-0.5">
          <CreateActionMenuItem
            testId="create-http-request-action-button"
            isUnavailable={hasReachedActionLimit}
            isPending={createHttpRequestActionMutation.isPending}
            label={t('toast.too_many_created_actions')}
            onClick={() => toggleModal(MODAL_NAMES.CREATE_HTTP_REQUEST_ACTION_DIALOG)}
          >
            <ToolIcon />
            {t('button.add_http_request_action')}
          </CreateActionMenuItem>

          <CreateActionMenuItem
            testId="create-remote-mcp-action-button"
            isUnavailable={hasReachedActionLimit}
            label={t('toast.too_many_created_actions')}
            onClick={handleCreateRemoteMCPAction}
          >
            <Network />
            {t('button.add_remote_mcp')}
          </CreateActionMenuItem>

          <CreateActionMenuItem
            testId="create-hubspot-action-button"
            isUnavailable={hasReachedActionLimit}
            label={t('toast.too_many_created_actions')}
            onClick={() => {
              capture(PostHogEventEnum.ActionCreated, { action_type: 'hubspot_template' });
              pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'hubspot_template' });
              toggleModal(MODAL_NAMES.HUBSPOT_TEMPLATE_GALLERY_DIALOG);
            }}
          >
            <HubSpotIcon className="size-4" fill="currentColor" />
            {t('button.add_hubspot_action')}
          </CreateActionMenuItem>

          <CreateActionMenuItem
            testId="create-discord-action-button"
            isUnavailable={hasReachedActionLimit}
            label={t('toast.too_many_created_actions')}
            onClick={() => {
              capture(PostHogEventEnum.ActionCreated, { action_type: 'discord_template' });
              pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'discord_template' });
              toggleModal(MODAL_NAMES.DISCORD_TEMPLATE_GALLERY_DIALOG);
            }}
          >
            <DiscordIcon className="size-4" />
            {t('button.add_discord_action')}
          </CreateActionMenuItem>

          <DropdownMenuItem
            data-testid="create-google-sheets-action-button"
            onClick={() => {
              capture(PostHogEventEnum.ActionCreated, { action_type: 'google_sheets' });
              pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'google_sheets' });
              toggleModal(MODAL_NAMES.GOOGLE_SHEETS_DIALOG);
            }}
          >
            <FileSpreadsheet />
            {t('google_sheets.title')}
          </DropdownMenuItem>

          {isQuickchatEmail(user?.email || '') && (
            <CreateActionMenuItem
              testId="create-knowledge-base-action-button"
              isUnavailable={hasReachedActionLimit}
              isPending={createKnowledgeBaseActionMutation.isPending}
              label={t('toast.too_many_created_actions')}
              onClick={handleCreateKnowledgeBaseAction}
            >
              <BookOpen />
              {t('button.add_knowledge_base_action')}
            </CreateActionMenuItem>
          )}

          <CreateActionMenuItem
            testId="create-shopify-mcp-action-button"
            isUnavailable={hasShopifyAction}
            isPending={createShopifyMCPActionMutation.isPending}
            label={t('tooltip.multiple_shopify_mcp')}
            onClick={handleCreateShopifyMCPAction}
          >
            <ShoppingBasket />
            {t('button.add_shopify_mcp')}
          </CreateActionMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
